import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, Menu, MenuItem, Popover } from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useTheme } from "@mui/material/styles";
import { ReservationBlock } from "../../../features/reservations";
import { Reservation } from "../../reservations/types/Reservation";
import { isToday } from "date-fns";
import { alpha } from "@mui/material/styles";
import { useOverlapDetection } from "../../reservations/hooks/useOverlapDetection";
import {
  calcularEventosLimpieza,
  esDepartamentoExcluidoDeLimpieza,
  fechaLimpiezaCalendarioReserva,
  tieneContinuidadMismoHuesped,
  type GetMotivoDiaSinPersonal,
  type TipoLimpieza,
} from "../../cleaning/limpiezaUtils";
import type { CleaningStaffTask } from "../../cleaning/cleaningStaffApi";
import {
  CLEANING_STATUS_DETAIL_LABELS,
  CLEANING_STATUS_LABELS,
  motivoLabelsPorEstado,
  resumenInsumos,
} from "../../cleaning/cleaningStatus";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import {
  CLEANING_LABELS,
  CLEANING_NAMES,
} from "../utils/cleaningCalendarStyles";
import { formatDateKey, isDateOnlyInRange } from "../../shared/utils/dateUtils";
import { calendarPopoverPaperSx } from "../utils/calendarPopoverStyles";
import type { DepartmentUnavailability } from "../departmentAvailability";
import DepartmentAvailabilityBlock from "./DepartmentAvailabilityBlock";

function prioridadLimpieza(tipo: TipoLimpieza): number {
  if (tipo === "manual") return 3;
  if (tipo === "checkout") return 2;
  return 1;
}

function limpiezaLabel(tipo: TipoLimpieza): string {
  return CLEANING_LABELS[tipo];
}

function limpiezaNombre(tipo: TipoLimpieza): string {
  return `Limpieza ${CLEANING_NAMES[tipo].toLowerCase()}`;
}

function formatCleaningDate(fecha: string): string {
  const [year, month, day] = fecha.split("-");
  if (!year || !month || !day) return fecha;
  return `${day}/${month}/${year}`;
}

function formatCleaningDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type LimpiezaCalendario = {
  fecha: string;
  tipo: TipoLimpieza;
  cantidad: number;
  recambio: boolean;
  /** Día sin personal en la fecha del marcador (domingo/feriado). */
  motivoSinPersonal?: string;
  /** Checkout corrido: el día con personal al que se programó la limpieza. */
  fechaOperativa?: string;
  /** Lo que el personal marcó para esta limpieza (puede haber más de uno el mismo día). */
  registros: CleaningStaffTask[];
};

const MOTIVO_SIN_PERSONAL_LABEL: Record<string, string> = {
  domingo: "Domingo sin personal",
  feriado: "Feriado sin personal",
};


interface DepartmentRowProps {
  depto: string;
  rowHeight: number;
  dayColumnWidth: number;
  diasDelCalendario: Date[];
  reservas: Reservation[];
  calcularPosicionReservaPx: (
    reserva: Reservation,
    dias: Date[],
    dayWidth: number,
    rowHeight: number,
    rowIndex: number
  ) => {
    leftPx: number;
    widthPx: number;
    topPx: number;
    startsOutsideMonth: boolean;
    endsOutsideMonth: boolean;
  };
  onReservationClick: (reserva: Reservation) => void;
  onCellClick: (fecha: Date, departamento: string) => void;
  getMotivoDiaSinPersonal?: GetMotivoDiaSinPersonal;
  fechaReferenciaLimpieza?: string;
  registrosLimpieza?: CleaningStaffTask[];
  availabilityBlocks?: DepartmentUnavailability[];
  canManageAvailability?: boolean;
  onBlockCell?: (fecha: Date, departamento: string) => void;
  onEditAvailability?: (block: DepartmentUnavailability) => void;
  onDeleteAvailability?: (block: DepartmentUnavailability) => void;
}

const DepartmentRow: React.FC<DepartmentRowProps> = ({
  depto,
  rowHeight,
  dayColumnWidth,
  diasDelCalendario,
  reservas,
  calcularPosicionReservaPx,
  onReservationClick,
  onCellClick,
  getMotivoDiaSinPersonal,
  fechaReferenciaLimpieza,
  registrosLimpieza = [],
  availabilityBlocks = [],
  canManageAvailability = false,
  onBlockCell,
  onEditAvailability,
  onDeleteAvailability,
}: DepartmentRowProps) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCellDate, setSelectedCellDate] = useState<Date | null>(null);
  const [cleaningAnchor, setCleaningAnchor] = useState<null | HTMLElement>(null);
  const [selectedCleaning, setSelectedCleaning] = useState<LimpiezaCalendario | null>(null);
  const { isMobile } = useBreakpoint();
  const todayCellBg = alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.34 : 0.22
  );

  // Filtrar reservas solo para este departamento
  const reservasDelDepartamento = useMemo(
    () => reservas.filter((reserva) => reserva && reserva.departamento === depto),
    [reservas, depto]
  );

  // Detectar reservas superpuestas usando el hook personalizado
  const { isReservationOverlapping } = useOverlapDetection(
    reservasDelDepartamento
  );

  const limpiezasDelDepartamento = useMemo(() => {
    const eventos = new Map<string, LimpiezaCalendario>();

    if (esDepartamentoExcluidoDeLimpieza(depto)) return [];

    // Fechas registradas por reserva: una automática pasada que se hizo no se oculta.
    const fechasRegistradasPorReserva = new Map<number, string[]>();
    for (const registro of registrosLimpieza) {
      if (registro.reserva_id == null) continue;
      const fechas = fechasRegistradasPorReserva.get(registro.reserva_id) || [];
      for (const fecha of [registro.fecha_limpieza, registro.fecha_original]) {
        if (fecha) fechas.push(fecha);
      }
      fechasRegistradasPorReserva.set(registro.reserva_id, fechas);
    }

    for (const reserva of reservasDelDepartamento) {
      if (!reserva.entrada || !reserva.salida) continue;
      const esContinuidadMismoHuesped = tieneContinuidadMismoHuesped(reserva, reservasDelDepartamento);
      const fechasConRegistro = fechasRegistradasPorReserva.get(reserva.id);

      for (const limpieza of calcularEventosLimpieza(
        reserva.entrada,
        reserva.salida,
        reserva.dias_limpieza_pactados,
        {
          ...(getMotivoDiaSinPersonal ? { getMotivoDiaSinPersonal } : {}),
          ...(fechaReferenciaLimpieza ? { fechaReferencia: fechaReferenciaLimpieza } : {}),
          ...(reserva.sin_limpieza_automatica ? { sinLimpiezaAutomatica: true } : {}),
          ...(fechasConRegistro ? { fechasConRegistro } : {}),
        }
      )) {
        // El calendario ubica los marcadores por fecha y no por bloque de reserva:
        // un checkout corrido a un día con personal se dibuja en su día de salida
        // (en el día al que se corrió pintaría sobre la reserva entrante), y el
        // globito cuenta cuándo se limpia o quién lo hizo.
        const tipoCalendario: TipoLimpieza =
          limpieza.tipo === "checkout" && esContinuidadMismoHuesped ? "intermedia" : limpieza.tipo;
        const esCheckoutDeContinuidad = limpieza.tipo === "checkout" && esContinuidadMismoHuesped;
        const fechaCalendario = fechaLimpiezaCalendarioReserva(
          { ...limpieza, tipo: tipoCalendario },
          reserva.salida,
          esCheckoutDeContinuidad
        );
        if (!fechaCalendario) continue;

        const existente = eventos.get(fechaCalendario);
        const motivoSinPersonal = limpieza.motivo_reprogramacion || limpieza.advertencia_sin_personal;
        const fechaOperativa = fechaCalendario !== limpieza.fecha ? limpieza.fecha : undefined;

        if (!existente) {
          eventos.set(fechaCalendario, {
            fecha: fechaCalendario,
            tipo: tipoCalendario,
            cantidad: 1,
            recambio: Boolean(
              reserva.dias_limpieza_recambio?.includes(fechaCalendario)
            ),
            ...(motivoSinPersonal ? { motivoSinPersonal } : {}),
            ...(fechaOperativa ? { fechaOperativa } : {}),
            registros: [],
          });
          continue;
        }

        existente.cantidad += 1;
        if (reserva.dias_limpieza_recambio?.includes(fechaCalendario)) {
          existente.recambio = true;
        }
        if (prioridadLimpieza(tipoCalendario) > prioridadLimpieza(existente.tipo)) {
          existente.tipo = tipoCalendario;
          existente.fechaOperativa = fechaOperativa;
        }
        if (motivoSinPersonal && !existente.motivoSinPersonal) {
          existente.motivoSinPersonal = motivoSinPersonal;
        }
      }
    }

    // Un registro (lo que el personal marcó) se pega al marcador que la reserva ya
    // puso; nunca crea uno ni le cambia el tipo. Un checkout corrido se registra el
    // día en que se limpió, pero su marcador está en el día de salida: se busca
    // por las dos fechas. Sin marcador (p. ej. una pactada que se quitó), el
    // registro sigue en la sección Limpieza y en el historial, no acá.
    for (const registro of registrosLimpieza) {
      const evento =
        (registro.fecha_limpieza && eventos.get(registro.fecha_limpieza))
        || (registro.fecha_original && eventos.get(registro.fecha_original));
      if (evento) evento.registros.push(registro);
    }
    // Si un día tiene más de un registro (p. ej. una intermedia marcada antes de que
    // ese día pasara a pactada), el más reciente es el que manda.
    for (const evento of eventos.values()) {
      evento.registros.sort((a, b) => (b.marcado_at || "").localeCompare(a.marcado_at || ""));
    }

    return Array.from(eventos.values());
  }, [depto, fechaReferenciaLimpieza, getMotivoDiaSinPersonal, registrosLimpieza, reservasDelDepartamento]);

  const limpiezaPorFecha = useMemo(
    () => new Map(limpiezasDelDepartamento.map((limpieza) => [limpieza.fecha, limpieza])),
    [limpiezasDelDepartamento]
  );

  const fechasConReservaActiva = useMemo(() => {
    const fechas = new Set<string>();

    for (const dia of diasDelCalendario) {
      const fechaKey = formatDateKey(dia);
      const tieneReserva = reservasDelDepartamento.some((reserva) =>
        isDateOnlyInRange(dia, reserva.entrada, reserva.salida)
      );
      if (tieneReserva) fechas.add(fechaKey);
    }

    return fechas;
  }, [diasDelCalendario, reservasDelDepartamento]);

  const fechasBloqueadas = useMemo(() => {
    const result = new Set<string>();
    for (const dia of diasDelCalendario) {
      if (availabilityBlocks.some((block) =>
        isDateOnlyInRange(dia, block.desde, block.disponible_nuevamente)
      )) {
        result.add(formatDateKey(dia));
      }
    }
    return result;
  }, [availabilityBlocks, diasDelCalendario]);

  const tieneReservaActivaEnFecha = useCallback(
    (fecha: Date) => fechasConReservaActiva.has(formatDateKey(fecha)),
    [fechasConReservaActiva]
  );

  const handleCellClick = (e: React.MouseEvent, dia: Date) => {
    // Si el clic fue en una reserva, no hacer nada
    if (e.target instanceof Element && e.target.closest(".reservation-block")) {
      return;
    }

    if (isMobile) {
      if (!tieneReservaActivaEnFecha(dia) && !fechasBloqueadas.has(formatDateKey(dia))) {
        onCellClick(dia, depto);
      }
      return;
    }

    // Si la celda no tiene reserva activa, mostrar el menú
    if (!tieneReservaActivaEnFecha(dia) && !fechasBloqueadas.has(formatDateKey(dia))) {
      setSelectedCellDate(dia);
      setMenuAnchor(e.currentTarget);
    }
  };

  const handleMenuItemClick = () => {
    if (selectedCellDate) {
      onCellClick(selectedCellDate, depto);
    }
    setMenuAnchor(null);
  };

  const handleBlockCell = () => {
    if (selectedCellDate) onBlockCell?.(selectedCellDate, depto);
    setMenuAnchor(null);
  };

  const handleCleaningClick = (
    event: React.MouseEvent<HTMLElement>,
    limpieza: LimpiezaCalendario
  ) => {
    event.stopPropagation();
    setSelectedCleaning(limpieza);
    setCleaningAnchor(event.currentTarget);
  };

  const handleCleaningClose = () => {
    setCleaningAnchor(null);
    setSelectedCleaning(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        height: rowHeight,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Columna de departamento */}
      <Box
        sx={{
          width: "70px",
          flexShrink: 0,
          p: 1,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "sticky",
          left: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "1px 0 2px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="body2" noWrap>
          {depto}
        </Typography>
      </Box>

      {/* Contenedor de celdas y reservas */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          position: "relative",
          touchAction: "pan-x pan-y",
        }}
      >
        {/* Celdas de días */}
        {diasDelCalendario.map((dia, idx) => {
          const tieneReserva = tieneReservaActivaEnFecha(dia);
          const estaBloqueado = fechasBloqueadas.has(formatDateKey(dia));
          const esHoy = isToday(dia);
          const esFinDeSemana = dia.getDay() === 0 || dia.getDay() === 6;

          return (
            <Box
              key={idx}
              role="gridcell"
              aria-label={`Depto ${depto}, ${formatDateKey(dia)}`}
              aria-disabled={tieneReserva || estaBloqueado}
              onClick={(e: React.MouseEvent) => handleCellClick(e, dia)}
              sx={{
                width: dayColumnWidth,
                minWidth: dayColumnWidth,
                height: "100%",
                borderRight: "1px solid",
                borderColor: "divider",
                cursor: "default",
                bgcolor: esHoy
                  ? todayCellBg
                  : esFinDeSemana
                  ? theme.palette.action.hover
                  : theme.palette.background.paper,
                "&:hover": !tieneReserva && !estaBloqueado
                  ? {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }
                  : {},
                touchAction: "pan-x pan-y",
                position: "relative",
                zIndex: 1,
              }}
            />
          );
        })}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {availabilityBlocks.map((block) => (
            <DepartmentAvailabilityBlock
              key={block.id}
              block={block}
              department={depto}
              days={diasDelCalendario}
              dayWidth={dayColumnWidth}
              canManage={canManageAvailability}
              onEdit={onEditAvailability}
              onDelete={onDeleteAvailability}
            />
          ))}
        </Box>

        {/* Contenedor de reservas */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {reservasDelDepartamento.map((reserva) => {
            const {
              leftPx,
              widthPx,
              topPx,
              startsOutsideMonth,
              endsOutsideMonth,
            } = calcularPosicionReservaPx(
              reserva,
              diasDelCalendario,
              dayColumnWidth,
              rowHeight,
              0
            );

            return (
              <Box
                key={reserva.id}
                sx={{
                  position: "absolute",
                  left: `${leftPx}px`,
                  width: `${widthPx}px`,
                  top: `${topPx}px`,
                  height: "100%",
                  pointerEvents: "auto",
                  padding: "2px",
                }}
              >
                <ReservationBlock
                  reserva={reserva}
                  startsOutsideMonth={startsOutsideMonth}
                  endsOutsideMonth={endsOutsideMonth}
                  onClick={onReservationClick}
                  isOverlapping={isReservationOverlapping(reserva.id)}
                />
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          {diasDelCalendario.map((dia, idx) => {
            const limpieza = limpiezaPorFecha.get(formatDateKey(dia));
            if (!limpieza) return null;

            const color = theme.palette.app.cleaning[limpieza.tipo];
            const statusLabel = limpieza.registros[0]
              ? CLEANING_STATUS_LABELS[limpieza.registros[0].estado]
              : null;
            const label =
              limpieza.cantidad > 1
                ? String(limpieza.cantidad)
                : limpiezaLabel(limpieza.tipo);
            const left = idx * dayColumnWidth + dayColumnWidth / 2;

            return (
              <Box
                key={`${depto}-${limpieza.fecha}`}
                aria-label={`${statusLabel ? `${statusLabel}. ` : ""}${limpiezaNombre(limpieza.tipo)}${limpieza.recambio ? " con recambio de huésped" : ""} en departamento ${depto}, ${formatCleaningDate(limpieza.fecha)}`}
                onClick={(event) => handleCleaningClick(event, limpieza)}
                sx={{
                  position: "absolute",
                  left,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: color,
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.92)",
                  boxShadow: limpieza.recambio
                    ? `0 0 0 2px ${theme.palette.warning.main}`
                    : `0 0 0 1px ${alpha(color, 0.28)}`,
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  cursor: "pointer",
                  pointerEvents: "auto",
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Popover
        open={Boolean(cleaningAnchor)}
        anchorEl={cleaningAnchor}
        onClose={handleCleaningClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{ sx: calendarPopoverPaperSx(theme) }}
      >
        {selectedCleaning && (
          <Box sx={{ display: "grid", gap: 0.35 }}>
            <Typography variant="body2" fontWeight={700}>
              Depto {depto} · {limpiezaNombre(selectedCleaning.tipo)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCleaningDate(selectedCleaning.fecha)}
              {selectedCleaning.cantidad > 1
                ? ` · ${selectedCleaning.cantidad} limpiezas ese día`
                : ""}
            </Typography>
            {selectedCleaning.recambio && (
              <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                Recambio de huésped
              </Typography>
            )}
            {selectedCleaning.motivoSinPersonal && selectedCleaning.registros.length === 0 && (
              <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                {MOTIVO_SIN_PERSONAL_LABEL[selectedCleaning.motivoSinPersonal] || "Día sin personal"}
                {selectedCleaning.fechaOperativa
                  ? ` · programada para el ${formatCleaningDate(selectedCleaning.fechaOperativa)}`
                  : " · la hace el responsable"}
              </Typography>
            )}
            {selectedCleaning.registros.map((registro) => (
              <Box key={registro.task_key} sx={{ display: "grid", gap: 0.2, mt: 0.3 }}>
                <Typography variant="caption" color="text.primary" fontWeight={700}>
                  {CLEANING_STATUS_DETAIL_LABELS[registro.estado]}
                  {registro.fecha_limpieza && registro.fecha_limpieza !== selectedCleaning.fecha
                    ? ` · el ${formatCleaningDate(registro.fecha_limpieza)}`
                    : ""}
                </Typography>
                {registro.motivo_no_realizada && (
                  <Typography variant="caption" color="text.secondary">
                    {motivoLabelsPorEstado(registro.estado)[registro.motivo_no_realizada]
                      || registro.motivo_no_realizada}
                  </Typography>
                )}
                {resumenInsumos(registro) && (
                  <Typography variant="caption" color="text.secondary">
                    Recargaron: {resumenInsumos(registro)}
                  </Typography>
                )}
                {registro.nota && (
                  <Typography variant="caption" color="text.secondary">
                    {registro.nota}
                  </Typography>
                )}
                {(registro.marcado_por?.nombre || registro.marcado_at) && (
                  <Typography variant="caption" color="text.secondary">
                    {[registro.marcado_por?.nombre, formatCleaningDateTime(registro.marcado_at)]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Popover>

      {/* Menú contextual */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
      >
        <MenuItem onClick={handleMenuItemClick}>Nueva reserva</MenuItem>
        {canManageAvailability && (
          <MenuItem onClick={handleBlockCell}>
            <EventBusyIcon fontSize="small" sx={{ mr: 1, color: theme.palette.app.availability.outOfService }} />
            Marcar fuera de servicio
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default React.memo(DepartmentRow);
